import { Divider, List, ListItem, Skeleton, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import {
  spacing,
  typography,
  colors,
} from "../../../../../../../../assets/styles/tokens";
import {
  formatValue,
  getCurrencySymbol,
  calcDiffPercent,
  OutputMetaField,
} from "../../../../../helpers/bizDevelopmentDashboardHelpers";

interface PerformanceItemMoreMetricsProps {
  rawRow: Record<string, any>;
  isComparisonActive: boolean;
  isComparisonLoading: boolean;
  comparisonRow?: Record<string, any>;
  outputMetadata: Record<string, OutputMetaField>;
}

// Keys already displayed prominently in the card body — exclude from More Metrics
const CARD_SHOWN_KEYS = new Set([
  "brand_display_name",
  "brand_id",
  "total_income_usd",
  "cpa_income_usd",
  "revshare_income_usd",
  "reg",
  "clicks",
  "ftd",
  "qftd",
  "ftd_incent",
]);

const PERC_SUFFIX = "_perc";

const getTitle = (key: string, meta: Record<string, OutputMetaField>): string => {
  const entry = meta[key];
  if (entry?.title) return entry.title.replace(/_/g, " ").toUpperCase();
  return key.replace(/_/g, " ").toUpperCase();
};

const fmtMetric = (apiKey: string, value: any): string => {
  if (value === null || value === undefined) return "N/A";
  if (apiKey === "roi") {
    const str = formatValue(value);
    return str === "N/A" ? "N/A" : `${str}%`;
  }
  const symbol = getCurrencySymbol(apiKey);
  const str = formatValue(value);
  if (str === "N/A") return "N/A";
  return symbol ? `${symbol}${str}` : str;
};

const PerformanceItemMoreMetrics = ({
  rawRow,
  isComparisonActive,
  isComparisonLoading,
  comparisonRow,
  outputMetadata,
}: PerformanceItemMoreMetricsProps) => {
  // Comparison was requested, data loaded, but no row for this brand
  const isMissingComp = isComparisonActive && !comparisonRow && !isComparisonLoading;

  // Derive keys dynamically from outputMetadata — same logic as buildColumns on desktop
  const moreMetricKeys = Object.keys(outputMetadata)
    .filter((key) => !CARD_SHOWN_KEYS.has(key) && !key.endsWith(PERC_SUFFIX))
    .sort((a, b) => Number(outputMetadata[a].order) - Number(outputMetadata[b].order));

  return (
    <Stack>
      <List sx={{ pb: 0, color: colors.GrayLightMode[900].$value }}>
        {moreMetricKeys.map((key, i) => {
          const label = getTitle(key, outputMetadata);
          const primaryRaw = rawRow[key];
          const compRaw = comparisonRow?.[key];

          const primaryDisplay = fmtMetric(key, primaryRaw);
          const compDisplay =
            isComparisonActive && comparisonRow
              ? fmtMetric(key, compRaw)
              : null;

          const diff =
            isComparisonActive && comparisonRow
              ? calcDiffPercent(primaryRaw, compRaw)
              : null;
          const diffText =
            diff !== null ? `${diff >= 0 ? "+" : ""}${diff.toFixed(1)}%` : null;
          const diffColor =
            diff !== null
              ? diff >= 0
                ? colors.Success[600].$value
                : colors.Error[600].$value
              : undefined;

          return (
            <Box key={key}>
              <ListItem
                sx={{
                  width: "100%",
                  display: "block",
                  p: spacing.Spacing.sm.$value,
                }}
              >
                {/* Primary row: label + primary value */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography
                    sx={{
                      fontSize: typography.Size.Text.xs.$value,
                      lineHeight: typography.Line_height.text.xs.$value,
                      color: colors.GrayLightMode[700].$value,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: typography.Font_weight.Semibold.$value,
                      fontSize: typography.Size.Text.sm.$value,
                      color: colors.GrayLightMode[900].$value,
                    }}
                  >
                    {primaryDisplay}
                  </Typography>
                </Box>

                {/* Comparison row: skeleton while loading, diff%+value when loaded, No Data if missing */}
                {isComparisonActive && isComparisonLoading && (
                  <Skeleton variant="text" width={60} sx={{ mt: "2px" }} />
                )}
                {isComparisonActive && comparisonRow && !isComparisonLoading && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "2px" }}>
                    <Typography
                      sx={{
                        fontSize: typography.Size.Text.xs.$value,
                        fontWeight: typography.Font_weight.Medium.$value,
                        color: diffColor ?? colors.GrayLightMode[500].$value,
                      }}
                    >
                      {diffText ?? ""}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: typography.Size.Text.xs.$value,
                        color: colors.GrayLightMode[500].$value,
                        fontWeight: typography.Font_weight.Regular.$value,
                      }}
                    >
                      {compDisplay}
                    </Typography>
                  </Box>
                )}

                {isMissingComp && (
                  <Typography
                    sx={{
                      fontSize: typography.Size.Text.xs.$value,
                      color: colors.GrayLightMode[400].$value,
                      mt: "2px",
                    }}
                  >
                    No Data
                  </Typography>
                )}
              </ListItem>
              {i < moreMetricKeys.length - 1 && <Divider />}
            </Box>
          );
        })}
      </List>
    </Stack>
  );
};

export default PerformanceItemMoreMetrics;
